import { AlertTriangle, Briefcase, PhoneCall, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type Tone = 'action' | 'rule' | 'risk' | 'consequence' | 'check';
type NoteDef = { tone: Tone; labelKey: string; textKey: string };
type ItemDef = { id: string; titleKey: string; textKey: string; notes: NoteDef[] };

const section1: ItemDef[] = [
  {
    id: 's1i1',
    titleKey: 'security.s1i1Title',
    textKey: 'security.s1i1Text',
    notes: [{ tone: 'action', labelKey: 'security.labelAction', textKey: 'security.s1i1Action' }],
  },
  {
    id: 's1i2',
    titleKey: 'security.s1i2Title',
    textKey: 'security.s1i2Text',
    notes: [{ tone: 'action', labelKey: 'security.labelAction', textKey: 'security.s1i2Action' }],
  },
  {
    id: 's1i3',
    titleKey: 'security.s1i3Title',
    textKey: 'security.s1i3Text',
    notes: [{ tone: 'action', labelKey: 'security.labelAction', textKey: 'security.s1i3Action' }],
  },
];

const section2: ItemDef[] = [
  {
    id: 's2i1',
    titleKey: 'security.s2i1Title',
    textKey: 'security.s2i1Text',
    notes: [
      { tone: 'consequence', labelKey: 'security.labelConsequence', textKey: 'security.s2i1Consequence' },
      { tone: 'rule', labelKey: 'security.labelRule', textKey: 'security.s2i1Rule' },
    ],
  },
  {
    id: 's2i2',
    titleKey: 'security.s2i2Title',
    textKey: 'security.s2i2Text',
    notes: [{ tone: 'risk', labelKey: 'security.labelRisk', textKey: 'security.s2i2Risk' }],
  },
  {
    id: 's2i3',
    titleKey: 'security.s2i3Title',
    textKey: 'security.s2i3Text',
    notes: [{ tone: 'check', labelKey: 'security.labelCheck', textKey: 'security.s2i3Check' }],
  },
];

const toneStyles: Record<Tone, string> = {
  action: 'bg-amber-50 border-amber-100 text-amber-900',
  rule: 'bg-emerald-50 border-emerald-100 text-emerald-900',
  risk: 'bg-rose-50 border-rose-100 text-rose-900',
  consequence: 'bg-rose-50 border-rose-100 text-rose-900',
  check: 'bg-sky-50 border-sky-100 text-sky-900',
};

const SectionBlock = ({
  number,
  icon: Icon,
  titleKey,
  subtitleKey,
  items,
}: {
  number: number;
  icon: typeof ShieldCheck;
  titleKey: string;
  subtitleKey: string;
  items: ItemDef[];
}) => {
  const { t } = useTranslation();
  return (
    <section>
      <div className='flex items-center gap-3 md:gap-4 mb-2'>
        <div className='flex items-center justify-center w-10 h-10 rounded-lg bg-amber-50 shrink-0'>
          <Icon size={22} className='text-amber-500' />
        </div>
        <h2 className='text-base md:text-lg font-semibold text-gray-900 leading-tight'>
          {t('security.sectionPrefix')} {number}. {t(titleKey)}
        </h2>
      </div>
      <p className='text-sm text-gray-500 mb-5 md:ml-14'>{t(subtitleKey)}</p>

      <div className='flex flex-col gap-4'>
        {items.map(item => (
          <div key={item.id} className='p-4 md:p-5 rounded-xl border border-gray-100 bg-gray-50/50'>
            <p className='text-base font-semibold text-gray-900 mb-2'>{t(item.titleKey)}</p>
            <p className='text-sm text-gray-700 leading-relaxed mb-3'>{t(item.textKey)}</p>
            <div className='flex flex-col gap-2'>
              {item.notes.map(note => (
                <div
                  key={note.labelKey + note.textKey}
                  className={`flex max-sm:flex-col gap-0.5 sm:gap-2 p-3 rounded-lg border ${toneStyles[note.tone]}`}
                >
                  <span className='text-sm font-semibold shrink-0'>{t(note.labelKey)}:</span>
                  <span className='text-sm leading-relaxed'>{t(note.textKey)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

function Security() {
  const { t } = useTranslation();

  return (
    <div className='flex justify-center'>
      <div className='lg:w-[1130px] w-full max-lg:px-4'>
        <div className='max-md:p-5 p-8 w-full bg-white mb-12'>
          <div className='flex items-center gap-3 md:gap-4 mb-6 md:mb-8'>
            <div className='flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full bg-amber-50 shrink-0'>
              <ShieldCheck size={26} className='text-amber-500' />
            </div>
            <h1 className='text-base md:text-2xl font-semibold text-gray-900 leading-tight'>{t('security.title')}</h1>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            <SectionBlock
              number={1}
              icon={PhoneCall}
              titleKey='security.section1Title'
              subtitleKey='security.section1Subtitle'
              items={section1}
            />

            <SectionBlock
              number={2}
              icon={Briefcase}
              titleKey='security.section2Title'
              subtitleKey='security.section2Subtitle'
              items={section2}
            />
          </div>

          <div className='mt-8 flex gap-3 md:gap-4 p-4 md:p-5 rounded-xl bg-rose-50 border border-rose-100'>
            <AlertTriangle size={24} className='text-rose-600 shrink-0 mt-0.5' />
            <div className='flex-1'>
              <p className='text-base font-semibold text-rose-900 mb-3'>{t('security.alertTitle')}</p>
              <ol className='flex flex-col gap-2 list-decimal pl-4 md:pl-5'>
                <li className='text-sm text-rose-900/90 leading-relaxed'>{t('security.alertStep1')}</li>
                <li className='text-sm text-rose-900/90 leading-relaxed'>
                  {t('security.alertStep2Prefix')}{' '}
                  <a href='tel:446007035' className='font-semibold underline'>
                    446007035
                  </a>
                  ,{' '}
                  <a href='tel:2222' className='font-semibold underline'>
                    22-22
                  </a>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Security;
